import { Link, useParams } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';

export default function ArticleDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 md:pt-18 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">文章未找到</h1>
            <Link to="/articles" className="text-primary-600 hover:text-primary-700 font-medium">
              返回文章列表
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.slug !== article.slug && a.category === article.category)
    .slice(0, 2);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.date,
    url: `https://www.jiujiucha.com/articles/${article.slug}`,
    publisher: {
      '@type': 'Organization',
      name: '久久插',
      url: 'https://www.jiujiucha.com',
    },
  };

  return (
    <>
      <SEOHead
        title={`${article.title} | 久久插技术文章`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://www.jiujiucha.com/articles/${article.slug}`}
        ogType="article"
        schema={articleSchema}
      />

      <Navbar />

      <main className="pt-16 md:pt-18">
        <div className="bg-background-100 border-b border-background-200/60">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '技术文章', path: '/articles' },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="py-10 md:py-14">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="mb-8">
              <div className="flex items-center gap-3 text-xs text-foreground-500 mb-4 flex-wrap">
                <span className="px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium">
                  {article.category}
                </span>
                <span>{article.date}</span>
                <span>·</span>
                <span>{article.readTime}</span>
                <span>·</span>
                <span>{article.author}</span>
              </div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <p className="text-foreground-600 text-base md:text-lg leading-relaxed">
                {article.excerpt}
              </p>
            </div>

            <div className="relative rounded-xl overflow-hidden h-64 md:h-80 mb-10">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/60">
              <div className="flex flex-wrap gap-2 mb-8">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700 font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              {relatedArticles.length > 0 && (
                <div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">
                    相关文章
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {relatedArticles.map((ra) => (
                      <Link
                        key={ra.slug}
                        to={`/articles/${ra.slug}`}
                        className="group bg-background-100 rounded-xl p-5 border border-background-200/60 hover:border-accent-300/50 transition-all duration-200"
                      >
                        <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1 group-hover:text-primary-600 transition-colors">
                          {ra.title}
                        </h4>
                        <p className="text-xs text-foreground-500 line-clamp-2">{ra.excerpt}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-8">
                <Link
                  to="/articles"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回文章列表
                </Link>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}